import { formatIndian, formatNumber, roundTo } from './format';
import {
  type Expr,
  evaluateFormula,
  FormulaSyntaxError,
  parseFormula,
  references,
} from './formula';
import {
  describeRange,
  evaluate,
  type Flag,
  NO_FLAG,
  type PatientContext,
  rangeMinMax,
  selectRange,
} from './ranges';
import type { RangeSpec, TemplateDoc, TestRow } from './template';
import { numericOf, type ParsedValue, parseValue } from './values';

/** What the technician typed, keyed by row id. Calculated rows are never stored here. */
export type ResultInput = Record<string, string | undefined>;

export type RowIssue =
  | { kind: 'required'; message: string }
  | { kind: 'formula_missing'; message: string; missing: string[] }
  | { kind: 'formula_invalid'; message: string }
  | { kind: 'not_a_number'; message: string }
  | { kind: 'critical'; message: string };

export interface ComputedTestRow {
  kind: 'test';
  row: TestRow;
  calculated: boolean;
  /** Full-precision number used by formulas; null for text or empty. */
  raw: number | null;
  /** Exactly what prints in the Result column. */
  display: string;
  flag: Flag;
  range: RangeSpec | null;
  refText: string;
  refMin: string;
  refMax: string;
  issues: RowIssue[];
}

export interface ComputedOtherRow {
  kind: 'section' | 'note' | 'narrative';
  id: string;
  text: string;
}

export type ComputedRow = ComputedTestRow | ComputedOtherRow;

export interface ComputedSection {
  rows: ComputedRow[];
  /** Issues that must be fixed before the report can be signed. */
  blocking: { rowId: string; issue: RowIssue }[];
  /** Out-of-range values, in row order, for summaries and review screens. */
  flagged: ComputedTestRow[];
}

export interface ComputeOptions {
  /** Print large counts with Indian grouping (1,30,000). */
  indianGrouping?: boolean;
  narratives?: Record<string, string | undefined>;
}

/**
 * Compute every row of one template section: parse inputs, run formulas in dependency
 * order on full-precision values, round for display, then flag the displayed value.
 */
export function computeSection(
  doc: TemplateDoc,
  input: ResultInput,
  patient: PatientContext,
  opts: ComputeOptions = {},
): ComputedSection {
  const tests = doc.rows.filter((r): r is TestRow => r.kind === 'test');
  const byCode = new Map(tests.map((r) => [r.code, r]));

  const parsed = new Map<string, ParsedValue>();
  const env = new Map<string, number | null>();
  const formulaIssues = new Map<string, RowIssue>();
  const asts = new Map<string, Expr>();

  for (const r of tests) {
    if (r.formula) {
      try {
        asts.set(r.code, parseFormula(r.formula));
      } catch (e) {
        const msg = e instanceof FormulaSyntaxError ? e.message : 'Invalid formula';
        formulaIssues.set(r.id, { kind: 'formula_invalid', message: msg });
      }
      continue;
    }
    const v = parseValue(input[r.id]);
    parsed.set(r.id, v);
    env.set(r.code, numericOf(v));
  }

  for (const code of topoOrder(asts, byCode, formulaIssues)) {
    const r = byCode.get(code) as TestRow;
    const res = evaluateFormula(asts.get(code) as Expr, env);
    if (res.ok) {
      env.set(code, res.value);
      parsed.set(r.id, { kind: 'number', value: res.value });
    } else {
      env.set(code, null);
      parsed.set(r.id, { kind: 'empty' });
      if (res.reason === 'missing') {
        formulaIssues.set(r.id, {
          kind: 'formula_missing',
          message: `Needs ${res.missing.join(', ')}`,
          missing: res.missing,
        });
      } else {
        formulaIssues.set(r.id, {
          kind: 'formula_invalid',
          message: 'Cannot be calculated (division by zero?)',
        });
      }
    }
  }

  const rows: ComputedRow[] = [];
  const blocking: ComputedSection['blocking'] = [];
  const flagged: ComputedTestRow[] = [];

  for (const r of doc.rows) {
    if (r.kind === 'section') {
      rows.push({ kind: 'section', id: r.id, text: r.label });
      continue;
    }
    if (r.kind === 'note') {
      rows.push({ kind: 'note', id: r.id, text: r.text });
      continue;
    }
    if (r.kind === 'narrative') {
      rows.push({ kind: 'narrative', id: r.id, text: opts.narratives?.[r.id] ?? r.text });
      continue;
    }

    const issues: RowIssue[] = [];
    const fi = formulaIssues.get(r.id);
    if (fi) issues.push(fi);

    const value = parsed.get(r.id) ?? { kind: 'empty' };
    const raw = numericOf(value);
    const display = displayOf(r, value, input[r.id], opts);
    const printed = parseValue(display);

    if (r.required && value.kind === 'empty' && !r.formula) {
      issues.push({ kind: 'required', message: `${r.label} is required` });
    }
    if (r.valueType === 'numeric' && value.kind === 'text') {
      issues.push({ kind: 'not_a_number', message: `"${value.text}" is not a number` });
    }

    const range = selectRange(r.ranges, patient);
    const flag = range ? evaluate(printed, range, r.critical) : NO_FLAG;
    if (flag.critical)
      issues.push({ kind: 'critical', message: `${r.label} is at a critical level` });

    const refText = range
      ? describeRange(range)
      : r.ranges
          .map((x) => (x.sex ? `${x.sex}: ${describeRange(x)}` : describeRange(x)))
          .join('\n');
    const mm = range ? rangeMinMax(range) : { min: refText, max: '' };

    const computed: ComputedTestRow = {
      kind: 'test',
      row: r,
      calculated: Boolean(r.formula),
      raw,
      display,
      flag,
      range,
      refText,
      refMin: mm.min,
      refMax: mm.max,
      issues,
    };
    rows.push(computed);
    if (flag.code) flagged.push(computed);
    for (const issue of issues) {
      // A calculated row whose inputs are absent stays blank; only the inputs marked required block.
      if (issue.kind !== 'critical' && issue.kind !== 'formula_missing') {
        blocking.push({ rowId: r.id, issue });
      }
    }
  }

  return { rows, blocking, flagged };
}

function displayOf(
  r: TestRow,
  v: ParsedValue,
  typed: string | undefined,
  opts: ComputeOptions,
): string {
  if (v.kind === 'empty') return '';
  if (v.kind === 'number') {
    if (opts.indianGrouping && Math.abs(v.value) >= 100000)
      return formatIndian(v.value, r.decimals ?? 0);
    return r.decimals === undefined
      ? String(roundTo(v.value, 6))
      : formatNumber(v.value, r.decimals);
  }
  if (v.kind === 'censored') {
    const n = r.decimals === undefined ? String(v.value) : formatNumber(v.value, r.decimals);
    return `${v.comparator} ${n}`;
  }
  return (typed ?? '').trim();
}

/** Order calculated rows so each runs after the rows it depends on. Cycles become issues. */
function topoOrder(
  asts: Map<string, Expr>,
  byCode: Map<string, TestRow>,
  issues: Map<string, RowIssue>,
): string[] {
  const order: string[] = [];
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (code: string, trail: string[]): boolean => {
    if (state.get(code) === 'done') return true;
    if (state.get(code) === 'visiting') {
      const r = byCode.get(code) as TestRow;
      issues.set(r.id, {
        kind: 'formula_invalid',
        message: `Circular formula: ${[...trail, code].join(' → ')}`,
      });
      return false;
    }
    state.set(code, 'visiting');
    let ok = true;
    for (const dep of references(asts.get(code) as Expr)) {
      if (asts.has(dep)) ok = visit(dep, [...trail, code]) && ok;
    }
    state.set(code, 'done');
    if (ok) order.push(code);
    return ok;
  };
  for (const code of asts.keys()) visit(code, []);
  return order;
}
